import { formatMoney, themeColor } from '@/utils'
import { ThemeColor } from '@/enums'
import styled from 'styled-components'
import { getFinalPrices } from '@/modules/price/price'
import { Button, Skeleton } from '@surein/ui'
import { CarrierLogo, Typography } from '@/components'
import { useTranslation } from 'react-i18next'
import { Ban } from 'lucide-react'
import { LoadingIcon } from '@/icons'
import { GetRecommendationByIdApiResponse } from '@/api/recommendations'
import { isProductCustomizationMismatched } from '../Products/helpers'

const FloatingWrapper = styled.div<{ visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${themeColor(ThemeColor.b0)};
  padding: 8px 0;

  transform: translateY(${(props) => (props.visible ? '0' : '100%')});
  transition: transform 0.4s ease;

  box-shadow: 0 -1px 12px ${themeColor(ThemeColor.shadow1)};
`

const OffersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  max-width: 1000px;
  width: 100%;
  padding: 0 16px;
`

const OfferContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const LogoContainer = styled.div`
  max-height: 50px;
  max-width: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PriceContainer = styled.div<{ shouldShowMismatchWarning: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;

  border: 2px solid
    ${(props) =>
      props.shouldShowMismatchWarning
        ? themeColor(ThemeColor.warning1)
        : 'none'};
  padding: 16px;
  border-radius: 8px;
`

const CtaButtonContainer = styled.div`
  width: 100%;

  button {
    width: 100%;
  }
`

export const RecommendationFloatingCTA = ({
  recommendationData,
  visible,
  onCTAClick,
  isRecommendationReloading,
}: {
  recommendationData: GetRecommendationByIdApiResponse
  visible: boolean
  onCTAClick: (productId: string) => void
  isRecommendationReloading: boolean
}) => {
  const { t } = useTranslation()

  return (
    <FloatingWrapper visible={visible}>
      <OffersContainer>
        {recommendationData?.recommendationProducts.map((product, index) => {
          const shouldShowMismatchWarning = isProductCustomizationMismatched(
            recommendationData,
            product.recommendationProductId,
          )
          const isProductAvailable =
            !(product.status === 'error') ||
            product.isSearchEnginePrice === false
          const price = getFinalPrices(
            product.grossPrice,
            product.discountValue,
            product.paymentPeriod ?? 'yearly',
          )

          return (
            <OfferContainer key={product.recommendationProductId}>
              <LogoContainer>
                <CarrierLogo name={product.carrierData.logoUrl} />
              </LogoContainer>
              <PriceContainer
                shouldShowMismatchWarning={shouldShowMismatchWarning}
              >
                {isRecommendationReloading ? (
                  <Skeleton count={2} width="100%" height="14px" />
                ) : (
                  <>
                    <Typography bold color={ThemeColor.b100} variant="h4">
                      {isProductAvailable
                        ? formatMoney(price.finalPrice) || '----'
                        : '----,----'}
                    </Typography>
                    <Typography color={ThemeColor.b50} variant="badgeText">
                      / {t(`paymentPeriodOptions.${product.paymentPeriod}`)}
                    </Typography>
                  </>
                )}
              </PriceContainer>

              <CtaButtonContainer>
                <Button
                  variant={
                    index === 1 && isProductAvailable ? 'primary' : 'muted'
                  }
                  fullWidth
                  disabled={!isProductAvailable}
                  onClick={() => onCTAClick(product.recommendationProductId)}
                  shape="square"
                >
                  {isRecommendationReloading ? (
                    <LoadingIcon />
                  ) : isProductAvailable ? (
                    t('getCoverage')
                  ) : (
                    <Ban />
                  )}
                </Button>
              </CtaButtonContainer>
            </OfferContainer>
          )
        })}
      </OffersContainer>
    </FloatingWrapper>
  )
}
